import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button as MuiButton } from '@mui/material';

import { SubmitButton } from 'src/presentations/components/atom/button/SubmitButton';
import { useShipmentSelector } from '../ShipmentSelector';
import { ModalFooterProps, NoContractForm, NoContractStep } from './types';

export const NoContractFooter = (props: ModalFooterProps) => {
  const { activeStep, onSaveDraft, onSuccessSubmit, onGoBack, onConfirm, onClose, onSkip, isEditingCharge } = props;
  const { shipment } = useShipmentSelector();
  const formContext = useFormContext<NoContractForm>();
  const { watch } = formContext;
  const { contractChoice, verdict, invoiceCharges, reason } = watch();

  const startDate = watch('newMatchingService.existingShippingService.validityPeriod.start');
  const endDate = watch('newMatchingService.existingShippingService.validityPeriod.end');

  const isStepNewService = activeStep === NoContractStep.Step2_Resolution && verdict === 'setup-new';
  const isStepAcceptConfirm = activeStep === NoContractStep.Step2_Resolution && verdict === 'accept';

  const isShowDraftButton = verdict === 'setup-new' && (activeStep === NoContractStep.Step2_Resolution || activeStep === NoContractStep.Step2b_NewService);
  const isShowNextButton = activeStep !== NoContractStep.Step3_Summary && activeStep !== NoContractStep.Step1_Select && !isStepAcceptConfirm;
  const isDisableNextButton = useMemo(() => {
    if (activeStep === NoContractStep.Step1_Select && verdict) {
      return false;
    }
    if (activeStep === NoContractStep.Step2_Resolution && verdict === 'setup-new' && contractChoice) {
      return false;
    }

    if (activeStep === NoContractStep.Step2b_NewService && verdict === 'setup-new') {
      // Always enable Next button on Step2b_NewService regardless of dates or charges
      return false;
    }
    if (isStepAcceptConfirm) {
      const hasInvoiceCharges = shipment?.invoiceCharges && shipment.invoiceCharges.length > 0;
      if (!hasInvoiceCharges) {
        return false;
      }

      const charges = invoiceCharges || [];

      // Check if we have the same number of charges AND all charges have both fields filled
      if (charges.length === shipment.invoiceCharges.length) {
        const isValid = charges.every((charge) => charge.chargeCategory && charge.legType);

        // Check if any charge has decision === false (rejected)
        const hasRejectedCharges = charges.some((charge) => charge.decision === false);

        // If there are rejected charges, reason must be filled
        if (hasRejectedCharges && (!reason || reason.trim().length === 0)) {
          return true;
        }

        return !isValid;
      }

      // If lengths don't match, button should be disabled
      return true;
    }
    if (activeStep === NoContractStep.Step2_Resolution && verdict === 'reject') {
      // Enable button if reason is provided
      return !reason || reason.trim().length === 0;
    }
    return true;
  }, [activeStep, verdict, contractChoice, invoiceCharges, shipment, startDate, endDate, reason]);

  return (
    <div className='absolute bottom-0 left-0 right-0 bg-white z-10 shadow-[0_-2px_8px_2px_rgba(0,0,0,0.1)]'>
      <div className='px-24 py-20 border-t border-neutral-20 flex items-center'>
        <div className='flex items-center gap-16'>
          {activeStep === NoContractStep.Step1_Select && (
            <MuiButton type='button' variant='outlined' color='inherit' onClick={onClose} sx={{ textTransform: 'capitalize' }}>
              Close
            </MuiButton>
          )}
          {activeStep > NoContractStep.Step1_Select && (
            <MuiButton type='button' variant='outlined' color='inherit' onClick={onGoBack} sx={{ textTransform: 'capitalize' }}>
              Back
            </MuiButton>
          )}
        </div>
        <div className='ml-auto pl-12 flex items-center gap-16'>
          {isShowDraftButton && (
            <SubmitButton
              variant='outlined'
              color='inherit'
              disabled={isEditingCharge}
              onClick={formContext.handleSubmit(onSaveDraft)}
              sx={(theme) => ({
                ':disabled': {
                  color: theme.palette.common.black,
                  background: theme.palette.background.default,
                },
                textTransform: 'capitalize',
              })}
            >
              Save Draft & Close
            </SubmitButton>
          )}

          {activeStep > NoContractStep.Step1_Select && !isStepNewService && (
            <MuiButton type='button' variant='outlined' color='inherit' onClick={onClose} sx={{ textTransform: 'capitalize' }}>
              Close
            </MuiButton>
          )}

          {isStepNewService && (
            <MuiButton type='button' variant='outlined' color='inherit' onClick={onSkip} sx={{ textTransform: 'capitalize' }}>
              Skip
            </MuiButton>
          )}

          {isShowNextButton && (
            <SubmitButton
              disabled={isDisableNextButton || isEditingCharge}
              variant='contained'
              color='primary'
              onClick={formContext.handleSubmit(onSuccessSubmit)}
              sx={{
                textTransform: 'capitalize',
              }}
            >
              {isStepNewService ? 'Copy Charges' : 'Next Step'}
            </SubmitButton>
          )}

          {(activeStep === NoContractStep.Step3_Summary || isStepAcceptConfirm) && (
            <SubmitButton
              variant='contained'
              color={verdict === 'reject' ? 'error' : 'primary'}
              onClick={formContext.handleSubmit(onConfirm)}
              sx={{ textTransform: 'capitalize' }}
              disabled={isStepAcceptConfirm ? isDisableNextButton : false}
            >
              Confirm
            </SubmitButton>
          )}
        </div>
      </div>
    </div>
  );
};
